import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { Sumo } from "@/hooks/useSumos";

interface SumoFormDialogProps {
  trigger: React.ReactNode;
  title: string;
  onSave: (sumoData: Partial<Sumo>) => Promise<void>;
  sumo?: Partial<Sumo>;
}

export const SumoFormDialog = ({ trigger, title, onSave, sumo }: SumoFormDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Sumo>>(
    sumo || {
      title: '',
      description: '',
      price: '',
      features: [],
      specifications: {},
      is_visible: true
    }
  );

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFeature = () => {
    const features = Array.isArray(formData.features) ? formData.features : [];
    updateField('features', [...features, '']);
  };

  const updateFeature = (index: number, value: string) => {
    const features = Array.isArray(formData.features) ? formData.features : [];
    const newFeatures = [...features];
    newFeatures[index] = value;
    updateField('features', newFeatures);
  };

  const removeFeature = (index: number) => {
    const features = Array.isArray(formData.features) ? formData.features : [];
    const newFeatures = features.filter((_, i) => i !== index);
    updateField('features', newFeatures);
  };

  const handleSave = async () => {
    try {
      await onSave(formData);
      setOpen(false);
      // Reset form if creating new sumo
      if (!sumo) {
        setFormData({
          title: '',
          description: '',
          price: '',
          features: [],
          specifications: {},
          is_visible: true
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Titre</Label>
              <Input
                id="title"
                value={formData.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Nom du sumo"
              />
            </div>
            <div>
              <Label htmlFor="price">Prix</Label>
              <Input
                id="price"
                value={formData.price || ''}
                onChange={(e) => updateField('price', e.target.value)}
                placeholder="Ex: 50€"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Description du sumo"
              rows={3}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Caractéristiques incluses</Label>
              <Button type="button" variant="outline" size="sm" onClick={addFeature}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </div>
            <div className="space-y-2 mt-2">
              {Array.isArray(formData.features) && formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    placeholder="Ex: Costumes inclus"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeFeature(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {(!formData.features || formData.features.length === 0) && (
                <p className="text-sm text-muted-foreground">Aucune caractéristique ajoutée</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave}>
              Sauvegarder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};